/**
 * Helpers to filter the classes of the teacher dashboard by school cycle.
 * Each class is expected to carry cycleId, cycleLabel and isCurrentCycle.
 */

/**
 * Builds the unique list of school cycles from the teacher's classes.
 * The API already sends the classes ordered (cycle in progress first, then the most recent),
 * so the order of first appearance is kept on purpose.
 * @param {Array} classes
 * @returns {Array<{value: number, label: string, current: boolean}>}
 */
export const buildCycleOptions = (classes = []) => {
  const options = [];
  classes.forEach((item) => {
    if (
      !item.cycleId ||
      options.some((option) => option.value === item.cycleId)
    ) {
      return;
    }
    options.push({
      value: item.cycleId,
      label: item.cycleLabel,
      current: !!item.isCurrentCycle,
    });
  });
  return options;
};

/**
 * Builds the value of the cookie that remembers the cycle chosen by the user.
 * The cookie is shared by the whole browser and is not always cleared when the session ends
 * (closing the browser, expired session), so the user id goes along to avoid that the next
 * teacher inherits the cycle of the previous one.
 * @param {string|number} userId Session user
 * @param {string|number} cycleId Cycle chosen
 * @returns {string|null} "userId:cycleId", or null when there is no user to bind it to
 */
export const buildStoredCycle = (userId, cycleId) =>
  userId ? `${userId}:${cycleId}` : null;

/**
 * Reads the cycle id from the cookie value, only when it belongs to the session user.
 * A value without owner (old format) or from another user is ignored.
 * @param {string|null} [storedValue] Cookie value
 * @param {string|number} userId Session user
 * @returns {string|undefined} Cycle id, or undefined when it must not be used
 */
export const parseStoredCycle = (storedValue, userId) => {
  if (!storedValue || !userId) {
    return undefined;
  }
  const [owner, cycleId] = String(storedValue).split(":");
  return cycleId && owner === String(userId) ? cycleId : undefined;
};

/**
 * Resolves which cycle must be selected on load:
 * cycle stored by the user > cycle in progress > most recent cycle with classes.
 * @param {Array} options Result of buildCycleOptions
 * @param {string|number|null} [storedCycleId] Cycle id remembered from the user's last choice
 * @returns {object|null}
 */
export const resolveSelectedCycle = (options = [], storedCycleId) => {
  const stored = storedCycleId
    ? options.find((option) => String(option.value) === String(storedCycleId))
    : undefined;
  return (
    stored || options.find((option) => option.current) || options[0] || null
  );
};

/**
 * Keeps only the classes of a cycle.
 * Without a cycle (the API did not send the cycle info) every class is returned.
 * @param {Array} classes
 * @param {object|null} cycle
 * @returns {Array}
 */
export const filterClassesByCycle = (classes = [], cycle) =>
  cycle ? classes.filter((item) => item.cycleId === cycle.value) : classes;
